
import React from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Shield, 
  Zap, 
  CheckCircle, 
  ArrowRight, 
  Star,
  Globe,
  MessageSquare,
  CreditCard,
  Smartphone,
  BarChart3,
  Building2,
  TrendingUp,
  Award,
  Clock,
  Target,
  Palette,
  Settings,
  Lock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const Landing = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/80 backdrop-blur-lg border-b border-slate-200/60 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-violet-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
              Communitly
            </span>
          </div>
          <nav className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-slate-600 hover:text-blue-600 transition-colors">Features</a>
            <a href="#success-stories" className="text-slate-600 hover:text-blue-600 transition-colors">Success Stories</a>
            <a href="#pricing" className="text-slate-600 hover:text-blue-600 transition-colors">Pricing</a>
            <Button variant="outline" size="sm">Sign In</Button>
            <Button size="sm" className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700">
              Start Your Community
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <motion.div 
            className="text-center max-w-4xl mx-auto"
            initial="initial"
            animate="animate"
            variants={staggerChildren}
          >
            <motion.div variants={fadeInUp} className="mb-6">
              <Badge className="bg-blue-100 text-blue-700 border-blue-200 px-4 py-1.5 text-sm font-medium">
                🚀 Trusted by 50+ Communities Worldwide
              </Badge>
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 mb-6 leading-tight"
            >
              Build Trusted{' '}
              <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
                Community Marketplaces
              </span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-xl md:text-2xl text-slate-600 mb-10 leading-relaxed max-w-3xl mx-auto"
            >
              Connect your community with vetted local service providers. Create your own branded platform that strengthens local bonds and drives economic growth.
            </motion.p>
            
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-lg px-8 py-4 h-auto shadow-xl shadow-blue-500/25"
              >
                Start Your Community
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button variant="outline" size="lg" className="text-lg px-8 py-4 h-auto border-2">
                Explore Communities
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Social Proof Strip */}
      <section className="py-16 bg-white/60 backdrop-blur-sm">
        <div className="container mx-auto px-6 max-w-6xl">
          <motion.div 
            className="text-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <p className="text-slate-600 mb-8 text-lg">Powering thriving communities worldwide</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
              {[
                { metric: "150+", label: "Service Providers" },
                { metric: "2K+", label: "Community Members" },
                { metric: "98%", label: "Satisfaction Rate" },
                { metric: "50+", label: "Active Communities" }
              ].map((stat, index) => (
                <motion.div 
                  key={index}
                  className="text-center"
                  variants={fadeInUp}
                >
                  <div className="text-3xl font-bold text-blue-600 mb-1">{stat.metric}</div>
                  <div className="text-sm text-slate-600">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Why Communities Choose Communitly
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Built for communities that value trust, local impact, and professional service delivery.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            {[
              {
                icon: Shield,
                title: "Community-Vetted Trust",
                description: "Every service provider is approved and recommended by your local community members.",
                gradient: "from-green-500 to-emerald-600"
              },
              {
                icon: TrendingUp,
                title: "Local Economic Impact",
                description: "Strengthen your local economy by connecting neighbors with trusted service providers.",
                gradient: "from-blue-500 to-indigo-600"
              },
              {
                icon: Palette,
                title: "Custom Branding",
                description: "Each community gets their own customized platform with unique branding and domain.",
                gradient: "from-purple-500 to-violet-600"
              },
              {
                icon: Award,
                title: "Proven Success",
                description: "Our communities report 40% higher service satisfaction and 60% more local engagement.",
                gradient: "from-orange-500 to-red-600"
              }
            ].map((feature, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white/80 backdrop-blur-sm group">
                  <CardContent className="p-6 text-center">
                    <div className={`w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-2xl mx-auto mb-6 flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <feature.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">{feature.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-6 max-w-6xl">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Built for Every Stakeholder
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Simple processes that create value for communities, members, and service providers.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-12">
            {/* For Communities */}
            <motion.div 
              className="text-center"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <div className="mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl mx-auto mb-6 flex items-center justify-center">
                  <Building2 className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">For Communities</h3>
              </div>
              <div className="space-y-4">
                {[
                  "Create your branded community platform",
                  "Customize design and approve providers", 
                  "Invite members and watch it grow"
                ].map((step, index) => (
                  <div key={index} className="flex items-center text-left">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                      <span className="text-blue-600 font-semibold">{index + 1}</span>
                    </div>
                    <span className="text-slate-700">{step}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* For Members */}
            <motion.div 
              className="text-center"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <div className="mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-3xl mx-auto mb-6 flex items-center justify-center">
                  <Users className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">For Members</h3>
              </div>
              <div className="space-y-4">
                {[
                  "Join your local community platform",
                  "Discover vetted local service providers",
                  "Connect directly and get quality service"
                ].map((step, index) => (
                  <div key={index} className="flex items-center text-left">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                      <span className="text-green-600 font-semibold">{index + 1}</span>
                    </div>
                    <span className="text-slate-700">{step}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* For Providers */}
            <motion.div 
              className="text-center"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <div className="mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-violet-600 rounded-3xl mx-auto mb-6 flex items-center justify-center">
                  <Zap className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">For Providers</h3>
              </div>
              <div className="space-y-4">
                {[
                  "Apply to join community platforms",
                  "Get approved by community leaders",
                  "Grow your business with trusted referrals"
                ].map((step, index) => (
                  <div key={index} className="flex items-center text-left">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                      <span className="text-purple-600 font-semibold">{index + 1}</span>
                    </div>
                    <span className="text-slate-700">{step}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mubashirun Success Story */}
      <section id="success-stories" className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Success Stories That Inspire
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              See how communities like Mubashirun are transforming local service discovery and economic growth.
            </p>
          </motion.div>

          <motion.div 
            className="bg-gradient-to-br from-blue-50 to-violet-50 rounded-3xl p-8 md:p-12 shadow-xl"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="bg-blue-100 text-blue-700 border-blue-200 mb-6 text-sm px-3 py-1">
                  Featured Community
                </Badge>
                <h3 className="text-3xl font-bold text-slate-900 mb-4">
                  Mubashirun Community
                </h3>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  "Communitly helped us build a thriving marketplace with over 150 verified service providers and 2,000+ happy community members. Our local economy has never been stronger, and our members love the trusted connections they've made."
                </p>
                
                <div className="grid grid-cols-3 gap-6 mb-8">
                  {[
                    { metric: "150+", label: "Service Providers", color: "text-blue-600" },
                    { metric: "2K+", label: "Community Members", color: "text-green-600" },
                    { metric: "98%", label: "Satisfaction Rate", color: "text-purple-600" }
                  ].map((stat, index) => (
                    <div key={index} className="text-center">
                      <div className={`text-3xl font-bold ${stat.color} mb-1`}>{stat.metric}</div>
                      <div className="text-sm text-slate-600">{stat.label}</div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center">
                  <div className="flex -space-x-2 mr-4">
                    {[1,2,3,4].map(i => (
                      <div key={i} className="w-10 h-10 bg-gradient-to-br from-blue-400 to-violet-600 rounded-full border-2 border-white flex items-center justify-center">
                        <span className="text-white text-sm font-medium">M</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">Mubashirun Leadership Team</div>
                    <div className="text-sm text-slate-600">Community Founders</div>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <div className="bg-white rounded-2xl shadow-2xl p-6 transform rotate-2 hover:rotate-0 transition-transform duration-300">
                  <div className="h-64 bg-gradient-to-br from-blue-100 to-violet-100 rounded-xl flex items-center justify-center">
                    <div className="text-center">
                      <BarChart3 className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                      <div className="text-lg font-semibold text-slate-900">Community Growth</div>
                      <div className="text-sm text-slate-600">Real-time analytics dashboard</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Showcase */}
      <section id="features" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-6 max-w-6xl">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Enterprise-Grade Features
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Professional tools designed to help communities and service providers thrive together.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            {[
              {
                icon: Globe,
                title: "Custom Branding",
                description: "Fully customizable themes, colors, and your own domain for complete brand control.",
                gradient: "from-blue-500 to-indigo-600"
              },
              {
                icon: Shield,
                title: "Advanced Security",
                description: "Enterprise-grade security with data encryption and privacy protection.",
                gradient: "from-green-500 to-emerald-600"
              },
              {
                icon: MessageSquare,
                title: "Real-time Messaging",
                description: "Direct communication between community members and service providers with chat history.",
                gradient: "from-purple-500 to-violet-600"
              },
              {
                icon: CreditCard,
                title: "Subscription Management",
                description: "Flexible pricing tiers and automated payment processing for service providers.",
                gradient: "from-orange-500 to-red-600"
              },
              {
                icon: Smartphone,
                title: "Mobile-First Design",
                description: "Beautiful, responsive design that works perfectly on all devices and screen sizes.",
                gradient: "from-teal-500 to-cyan-600"
              },
              {
                icon: BarChart3,
                title: "Analytics Dashboard",
                description: "Comprehensive analytics and insights for community leaders and business owners.",
                gradient: "from-pink-500 to-rose-600"
              },
              {
                icon: Target,
                title: "Advanced Discounts",
                description: "Automated coupon generation and smart approval systems for business promotions.",
                gradient: "from-indigo-500 to-blue-600"
              },
              {
                icon: Settings,
                title: "Admin Tools",
                description: "Complete community management with user roles, permissions, and moderation tools.",
                gradient: "from-violet-500 to-purple-600"
              },
              {
                icon: Clock,
                title: "24/7 Support",
                description: "Round-the-clock technical support and community management assistance.",
                gradient: "from-emerald-500 to-green-600"
              }
            ].map((feature, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white/80 backdrop-blur-sm group">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 bg-gradient-to-br ${feature.gradient} rounded-2xl mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">{feature.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Choose the plan that fits your community size and growth goals. All plans include our core features.
            </p>
          </motion.div>

          <motion.div 
            className="grid lg:grid-cols-3 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            {/* Starter */}
            <motion.div variants={fadeInUp}>
              <Card className="relative border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Community Starter</h3>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-slate-900">$99</span>
                    <span className="text-slate-600">/month</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {[
                      "Up to 50 service providers",
                      "Custom branding & domain",
                      "Basic analytics dashboard",
                      "Community management tools",
                      "Email support",
                      "Mobile app access"
                    ].map((feature, index) => (
                      <li key={index} className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button className="w-full" variant="outline">
                    Start Free Trial
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Pro */}
            <motion.div variants={fadeInUp}>
              <Card className="relative border-0 shadow-xl hover:shadow-2xl transition-all duration-300 h-full bg-gradient-to-br from-blue-50 to-violet-50">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-gradient-to-r from-blue-600 to-violet-600 text-white px-4 py-1">
                    Most Popular
                  </Badge>
                </div>
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Community Pro</h3>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-slate-900">$199</span>
                    <span className="text-slate-600">/month</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {[
                      "Up to 200 service providers",
                      "Advanced analytics & insights",
                      "Priority support",
                      "Custom integrations",
                      "Advanced messaging features",
                      "Revenue sharing options",
                      "API access",
                      "Custom email templates"
                    ].map((feature, index) => (
                      <li key={index} className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button className="w-full bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700">
                    Start Free Trial
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Enterprise */}
            <motion.div variants={fadeInUp}>
              <Card className="relative border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Enterprise</h3>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-slate-900">Custom</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {[
                      "Unlimited service providers",
                      "White-label solution",
                      "Custom development",
                      "Dedicated support team",
                      "Advanced security features",
                      "Multi-community management",
                      "SLA guarantees",
                      "Training & onboarding"
                    ].map((feature, index) => (
                      <li key={index} className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button className="w-full" variant="outline">
                    Contact Sales
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Technical Excellence */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-blue-900 text-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Built for Scale & Security
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Enterprise-grade infrastructure that grows with your community.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            {[
              {
                icon: Lock,
                title: "99.9% Uptime",
                description: "Reliable infrastructure with enterprise SLA guarantees"
              },
              {
                icon: Shield,
                title: "SOC 2 Compliant",
                description: "Enterprise security standards and data protection"
              },
              {
                icon: Globe,
                title: "Global CDN",
                description: "Fast loading times worldwide with edge optimization"
              },
              {
                icon: Settings,
                title: "API First",
                description: "Complete API access for custom integrations"
              }
            ].map((feature, index) => (
              <motion.div 
                key={index}
                variants={fadeInUp}
                className="text-center"
              >
                <div className="w-16 h-16 bg-white/10 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-slate-300">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-violet-600 text-white">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Build Your Community?
            </h2>
            <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
              Join thousands of communities already connecting their members with trusted local service providers. Start your free trial today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8 py-4 h-auto shadow-xl"
              >
                Start Your Community
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="border-white text-white hover:bg-white hover:text-blue-600 text-lg px-8 py-4 h-auto"
              >
                Book a Demo
              </Button>
            </div>
            <p className="text-sm text-blue-200 mt-6">
              No credit card required • 14-day free trial • Setup assistance included
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-violet-600 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold">Communitly</span>
              </div>
              <p className="text-slate-400">
                Building stronger communities through trusted local service connections.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Security</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Status</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8 text-center text-slate-400">
            <p>&copy; 2024 Communitly. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
